import React, { useMemo, useState } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { Receipt as BoletaIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { usePlanillonStore } from '../store';
import type { ConceptoColumna, PlanillonRow } from '../types/planillon';
import BoletaPagoDialog from './BoletaPagoDialog';
import EditDetalleDialog from './EditDetalleDialog';

const formatMonto = (value: number | undefined) => {
  if (value === undefined || value === null) return '';
  if (value === 0) return '-';
  return value.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PlanillonGrid: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { planillonData, loading, selectedPlanilla } = usePlanillonStore();
  const [boletaOpen, setBoletaOpen] = useState(false);
  const [boletaTrabajadorId, setBoletaTrabajadorId] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<PlanillonRow | null>(null);

  const handleVerBoleta = (idTrabajador: number) => {
    setBoletaTrabajadorId(idTrabajador);
    setBoletaOpen(true);
  };

  const handleEditDetalle = (row: PlanillonRow) => {
    setEditRow(row);
    setEditOpen(true);
  };

  const columns: GridColDef[] = useMemo(() => {
    if (!planillonData) return [];

    const baseColumns: GridColDef[] = [
      {
        field: 'actions',
        headerName: '',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {(selectedPlanilla?.estado === 'BORRADOR' || selectedPlanilla?.estado === 'CALCULADA') && (
              <Tooltip title="Editar detalle / conceptos">
                <IconButton size="small" onClick={() => handleEditDetalle(params.row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Ver boleta de pago">
              <IconButton size="small" onClick={() => handleVerBoleta(params.row.idTrabajador)}>
                <BoletaIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'nombreTrabajador',
        headerName: 'Trabajador',
        width: 220,
        pinnable: true,
      },
      {
        field: 'documento',
        headerName: 'Documento',
        width: 110,
      },
      {
        field: 'area',
        headerName: 'Area',
        width: 120,
      },
      {
        field: 'diasLaborados',
        headerName: 'Días',
        width: 60,
        align: 'center',
        headerAlign: 'center',
      },
    ];

    // Columnas dinámicas por tipo de concepto
    const remuneraciones = planillonData.columnas.filter((c) => c.tipoConcepto === 'R');
    const descuentos = planillonData.columnas.filter((c) => c.tipoConcepto === 'D');
    const aportes = planillonData.columnas.filter((c) => c.tipoConcepto === 'A');

    const conceptoToColumn = (concepto: ConceptoColumna): GridColDef => ({
      field: `concepto_${concepto.idConcepto}`,
      headerName: concepto.descripcion,
      width: 110,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value, row) => row.conceptoMontos?.[concepto.idConcepto] ?? 0,
      valueFormatter: (value: number) => formatMonto(value),
    });

    const remColumns = remuneraciones.map(conceptoToColumn);
    const descColumns = descuentos.map(conceptoToColumn);
    const aporteColumns = aportes.map(conceptoToColumn);

    const totalColumns: GridColDef[] = [
      {
        field: 'totalRemuneraciones',
        headerName: 'Total Rem.',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatMonto(value),
        cellClassName: 'font-bold',
      },
      {
        field: 'totalDescuentos',
        headerName: 'Total Desc.',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatMonto(value),
        cellClassName: 'font-bold',
      },
      {
        field: 'totalAportes',
        headerName: 'Total Aport.',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatMonto(value),
      },
      {
        field: 'netoPagar',
        headerName: 'Neto a Pagar',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatMonto(value),
        cellClassName: 'font-bold text-primary',
      },
    ];

    return [...baseColumns, ...remColumns, ...descColumns, ...aporteColumns, ...totalColumns];
  }, [planillonData]);

  if (!planillonData) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
        <Typography color="text.secondary">
          Seleccione un periodo y una planilla para ver el planillón
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Resumen */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 3 }, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 2, py: 1, flex: { xs: '1 1 45%', sm: '1 1 auto' } }}>
          <Typography variant="caption" color="text.secondary">Trabajadores</Typography>
          <Typography variant="h6" fontWeight="bold">{planillonData.totales.cantidadTrabajadores}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: { xs: '1 1 45%', sm: '1 1 auto' } }}>
          <Typography variant="caption" color="text.secondary">Total Remuneraciones</Typography>
          <Typography variant="h6" fontWeight="bold" color="success.main">
            S/ {planillonData.totales.totalRemuneraciones.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
          </Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: { xs: '1 1 45%', sm: '1 1 auto' } }}>
          <Typography variant="caption" color="text.secondary">Total Descuentos</Typography>
          <Typography variant="h6" fontWeight="bold" color="error.main">
            S/ {planillonData.totales.totalDescuentos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
          </Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: { xs: '1 1 45%', sm: '1 1 auto' } }}>
          <Typography variant="caption" color="text.secondary">Total Aportes</Typography>
          <Typography variant="h6" fontWeight="bold" color="warning.main">
            S/ {planillonData.totales.totalAportes.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
          </Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: { xs: '1 1 45%', sm: '1 1 auto' } }}>
          <Typography variant="caption" color="text.secondary">Neto a Pagar</Typography>
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            S/ {planillonData.totales.totalNeto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
          </Typography>
        </Paper>
      </Box>

      {/* Grilla */}
      <Paper sx={{ height: { xs: 'calc(100vh - 360px)', sm: 'calc(100vh - 340px)' } }}>
        <DataGrid
          rows={planillonData.filas}
          columns={columns}
          getRowId={(row) => row.idPlanillaDetalle}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          disableRowSelectionOnClick
          density={isMobile ? 'compact' : 'standard'}
          onRowDoubleClick={(params) => {
            if (selectedPlanilla?.estado === 'BORRADOR' || selectedPlanilla?.estado === 'CALCULADA') {
              handleEditDetalle(params.row as PlanillonRow);
            }
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              fontSize: { xs: '0.8rem', sm: '0.75rem' },
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.8rem',
            },
            '& .font-bold': {
              fontWeight: 700,
            },
            '& .text-primary': {
              color: '#3b82f6',
            },
          }}
        />
      </Paper>

      {/* Dialog Boleta de Pago */}
      <BoletaPagoDialog
        open={boletaOpen}
        onClose={() => setBoletaOpen(false)}
        idPlanilla={selectedPlanilla?.idPlanilla ?? 0}
        idTrabajador={boletaTrabajadorId}
      />

      {/* Dialog Editar Detalle */}
      <EditDetalleDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        row={editRow}
      />
    </Box>
  );
};

export default PlanillonGrid;
